import React from 'react';
import { Typography, Box, Avatar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import './SupportHelpTable.css';
import SupportHelpTableSkeleton from '../../../SkeletonEffect/SupportHelpTableSkeleton/SupportHelpTableSkeleton';

const formatText = text => {
  if (!text) return text;
  return text
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const StatusColor = {
  pending: { color: '#E0AD14', bgcolor: '#FFFCF2', value: 'Pending' },
  resolved: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Resolved' },
  default: { color: '#949DA6', bgcolor: '#F8F9FA', value: 'Unknown' },
};

const UserRoleColor = {
  teacher: { color: '#BC8D00', bgcolor: '#FFF3CE', value: 'Instructor' },
  student: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Learner' },
  learner: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Learner' },
};

export function SupportHelpTable({ data, isLoading = false }) {
  const navigate = useNavigate();

  const handleRowClick = id => {
    const ticket = data.find(t => t.id === id);
    if (ticket) {
      navigate(`/admin/support-detail/${id}`, { state: { ticket } });
    }
  };

  if (isLoading) {
    return <SupportHelpTableSkeleton />;
  }

  return (
    <Box className="support-table-container">
      {!data || data.length === 0 ? (
        <Box className="support-empty-state">
          <Typography className="support-empty-text">No support tickets available</Typography>
        </Box>
      ) : (
        <table className="support-table">
          <thead>
            <tr>
              <th className="support-th">Ticket ID</th>
              <th className="support-th">User Name</th>
              <th className="support-th">User Role</th>
              <th className="support-th">Issue Category</th>
              <th className="support-th">Created on</th>
              <th className="support-th">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map(row => (
              <tr key={row.id} className="support-row" onClick={() => handleRowClick(row.id)}>
                <td className="support-td">#{row.id}</td>
                <td className="support-td">
                  <Box className="support-user-cell">
                    <Avatar
                      src={
                        row.user?.profile_image && row.user.profile_image.startsWith('http')
                          ? row.user.profile_image
                          : undefined
                      }
                      sx={{ width: 25, height: 25 }}
                      onError={e => {
                        e.target.style.display = 'none';
                      }}
                    >
                      {row.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                    <Typography className="support-name-text">
                      {row.user?.name || 'Unknown User'}
                    </Typography>
                  </Box>
                </td>
                <td className="support-td">
                  <Box
                    className="support-role-badge"
                    sx={{
                      backgroundColor:
                        UserRoleColor[row.user?.user_type?.toLowerCase()]?.bgcolor ||
                        UserRoleColor.learner.bgcolor,
                      color:
                        UserRoleColor[row.user?.user_type?.toLowerCase()]?.color ||
                        UserRoleColor.learner.color,
                      borderColor:
                        UserRoleColor[row.user?.user_type?.toLowerCase()]?.color ||
                        UserRoleColor.learner.color,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {UserRoleColor[row.user?.user_type?.toLowerCase()]?.value ||
                        UserRoleColor.learner.value}
                    </Typography>
                  </Box>
                </td>
                <td className="support-td">
                  <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {(row.issue_category || []).length > 0 && (
                      <Box className="support-category-tag">
                        {formatText(row.issue_category[0])}
                      </Box>
                    )}
                    {(row.issue_category || []).length > 1 && (
                      <Box className="support-category-tag">
                        +{(row.issue_category || []).length - 1}
                      </Box>
                    )}
                  </Box>
                </td>
                <td className="support-td support-td-date">
                  {row.created_at ? dayjs(row.created_at).format('DD-MM-YYYY') : '-'}
                </td>
                <td className="support-td">
                  <Box
                    className="support-status-badge"
                    sx={{
                      border: `1px solid ${
                        StatusColor[row.status?.toLowerCase()]
                          ? StatusColor[row.status.toLowerCase()].color
                          : StatusColor.default.color
                      }`,
                      backgroundColor: StatusColor[row.status?.toLowerCase()]
                        ? StatusColor[row.status.toLowerCase()].bgcolor
                        : StatusColor.default.bgcolor,
                      color: StatusColor[row.status?.toLowerCase()]
                        ? StatusColor[row.status.toLowerCase()].color
                        : StatusColor.default.color,
                    }}
                  >
                    <Box
                      className="support-status-dot"
                      sx={{
                        backgroundColor: StatusColor[row.status?.toLowerCase()]
                          ? StatusColor[row.status.toLowerCase()].color
                          : StatusColor.default.color,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {StatusColor[row.status?.toLowerCase()]
                        ? StatusColor[row.status.toLowerCase()].value
                        : formatText(row.status) || StatusColor.default.value}
                    </Typography>
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Box>
  );
}
